import { type WorkItem } from "@digits-graphql/frontend/graphql-bearer"
import { svgIconStyles, svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgDigitsLogoNoBackground } from "@digits-shared/components/SVGIcons/digits/DigitsLogoNoBackground.svg"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"

export const DETAIL_CONTENT_LEFT_SPACING = 18
export const DETAIL_COLUMN_ACTIVE_CLASSNAME = "active"

export interface WorkItemRowProps {
  workItem: WorkItem
}

export const DetailHeader = styled.div`
  color: ${colors.translucentSecondary80};
  font-weight: ${fonts.weight.normal};
  font-size: 10px;
  margin: 0 0 5px;

  b {
    font-weight: ${fonts.weight.medium};
    color: ${colors.secondary};
  }
`

export const DetailContainer = styled.div`
  font-size: 14px;
  position: relative;
`

export const DetailColumn = styled.div<{ animateHeader?: boolean; hideHeader?: boolean }>`
  flex: 1;
  font-size: 12px;

  ${({ animateHeader }) =>
    animateHeader &&
    css`
      ${DetailHeader} {
        position: absolute;
        top: 0;
        transform: translateY(-30%);
        opacity: 0;
        transition:
          transform 80ms ease-in-out,
          opacity 80ms ease-in-out;
      }

      ${DetailContainer}:hover,
      &.${DETAIL_COLUMN_ACTIVE_CLASSNAME} {
        ${DetailHeader} {
          transform: translateY(calc(-100% - 5px));
          opacity: 1;
        }
      }
    `}

  ${({ hideHeader }) =>
    hideHeader &&
    css`
      ${DetailHeader} {
        opacity: 0;
      }
    `}
`

export const ToHeader = styled(DetailHeader)`
  padding-left: ${DETAIL_CONTENT_LEFT_SPACING}px;
`

export const ToValueText = styled.div`
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-left: 2px;
`

export const ToPlaceholderText = styled(ToValueText)`
  color: ${colors.translucentSecondary80};
`

export const DetailsValue = styled.div`
  width: 100%;
  display: flex;
  font-weight: ${fonts.weight.medium};
  align-items: center;
  background: ${colors.translucentWhite20};
`

export const ClosedDetailsValue = styled(DetailsValue)`
  background: none;
`

export const ToValue = styled(DetailsValue)`
  color: ${colors.secondary};
  border-radius: 200px;
  padding: 6px 7px 6px ${DETAIL_CONTENT_LEFT_SPACING - 2}px;
  border: 0.5px solid ${colors.translucentSecondary70};
`

export const Chevron = styled(SvgChevronDown)<{ $inverted?: boolean }>`
  width: 18px;
  height: 18px;
  ${svgPathStyles(colors.primary, 1.5)};

  transform: rotate(${({ $inverted }) => ($inverted ? -180 : 0)}deg);
  transition: transform 400ms ease;
  border-radius: 100%;
  border-width: 0;
`

export const ToContainer = styled(DetailContainer)<{
  isUserProvided: boolean
  hasSuggestion?: boolean
}>`
  ${({ hasSuggestion }) =>
    hasSuggestion &&
    css`
      ${ToValue} {
        min-width: 245px;
      }
    `}

  ${({ isUserProvided }) =>
    isUserProvided
      ? css`
          ${ToHeader} {
            color: ${colors.secondary};
            filter: drop-shadow(0px 0px 6px ${colors.translucentSecondary60});
          }

          ${ToValue} {
            box-shadow: none;
          }

          ${Chevron} {
            ${svgPathStyles(colors.secondary, 1.5)};
          }
        `
      : css`
          ${ToValue} {
            color: ${colors.primary};
            border-color: ${colors.primary};
            box-shadow: 0 0 6px ${colors.translucentPrimary30};
          }
        `}
`

export const ProofDigitsLogo = styled(SvgDigitsLogoNoBackground)`
  ${svgIconStyles(colors.primary)}
  height: 11px;
  width: 11px;
  margin-right: 4px;
`
